import type { ReactElement } from 'react';

import { Box } from '@/components/Box';
import { Loader } from '@/components/Loader';
import { Typography } from '@/components/Typography';
import type { OrderAppDeliveryMode } from '@/graphql/orderApp/api';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { MyOrdersLayout } from '@/layouts/MyOrdersLayout';
import { getTaxMessageShort } from '@/lib/regions';
import { isWithdrawalExcludedOrderLine } from '@/lib/withdrawal/withdrawalExcludedProductTypes';
import * as AM from '@/messages/myAccount';
import * as PM from '@/messages/payment';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import type {
  OrderReturnViewProps,
  ReturnLineItemViewModel,
} from './orderReturnTypes';
import { ReturnConfirmation } from './ReturnConfirmation';
import { ReturnConfirmSubmitModal } from './ReturnConfirmSubmitModal';
import { ReturnLineItem } from './ReturnLineItem';
import { ReturnOrderSummary } from './ReturnOrderSummary';
import { ReturnSubmitFooter } from './ReturnSubmitFooter';
import { useReturnViewData } from './useReturnViewData';

export type { OrderReturnViewProps } from './orderReturnTypes';

export const OrderReturnView = (props: OrderReturnViewProps) => {
  const {
    baseFieldId,
    channelSlug,
    formatMessage,
    getLineEligibilityState,
    goBackToOrderDetails,
    handleRequestSubmit,
    handleSubmitRecaptchaExpired,
    imageLoader,
    isGuestMode,
    isMobileView,
    isReturnConfirmOpen,
    isSubmitDisabled,
    isSubmitted,
    isSubmitting,
    isWithdrawalEligibilityLoading,
    isWithdrawalFeatureDisabled,
    languageCode,
    lineReturnFields,
    orderId,
    orderLines,
    paths,
    performReturnSubmit,
    push,
    reasonOptions,
    registeredOutOfEuScopeBlocked,
    returnableLines,
    selectedLineIds,
    setIsReturnConfirmOpen,
    setLineSelected,
    setSubmitRecaptchaToken,
    state,
    submitErrorMessage,
    submitRecaptchaKey,
    updateLineField,
  } = useReturnViewData(props);

  if (isWithdrawalFeatureDisabled) {
    return <Loader data-testid='orderReturnWithdrawalDisabled' height={400} />;
  }

  if (!state.order || state.loading || (!isGuestMode && !orderId)) {
    return <Loader data-testid='orderReturnLoader' height={400} />;
  }

  const order = state.order;

  const fulfilledFulfillment = order.fulfillments?.find(
    (item) => item.status === 'FULFILLED',
  );
  const trackingNumber = fulfilledFulfillment?.trackingNumber;
  const deliveryMode = order.metadata.find(
    (item) => item.key === 'order.delivery_mode',
  )?.value as OrderAppDeliveryMode;
  const isHomeDelivery = deliveryMode === 'HOME_DELIVERY';

  const voucherType = order.voucher?.discountValueType;
  const taxMessage = getTaxMessageShort(order.channel.slug);

  if (registeredOutOfEuScopeBlocked) {
    return (
      <Loader data-testid='orderReturnOutOfEuScopeRedirect' height={400} />
    );
  }

  if (isSubmitted) {
    return (
      <ReturnConfirmation
        isGuestMode={isGuestMode}
        onBackToMyAccount={() => {
          push(paths.myAccount.myOrders.orderHistory.asPath());
        }}
        onBuyWatch={() => {
          push(paths.watches.all.asPath());
        }}
        orderBusinessId={order.businessId ?? ''}
      />
    );
  }

  return (
    <>
      <ReturnOrderSummary
        isHomeDelivery={isHomeDelivery}
        isMobileView={isMobileView}
        order={order}
        trackingNumber={trackingNumber}
      />

      <Box marginBottom={2} marginTop={2}>
        <Typography
          color='secondaryGrey'
          data-testid='OrderReturnViewCustomerServiceIntro'
          scale='p3'
          textAlign={isMobileView ? 'center' : 'left'}
        >
          {formatMessage(PM.paymentMessages.returnFlowCustomerServiceIntro)}
        </Typography>
      </Box>

      <Box paddingBottom={3} paddingTop={3}>
        {returnableLines.map((orderLine, index) => {
          if (!orderLine) {
            return null;
          }
          const lineId = orderLine.id;
          const { eligibilityLine, isEligible, isKnown } =
            getLineEligibilityState(lineId);
          const isExcluded = isWithdrawalExcludedOrderLine(orderLine);
          const isLineDisabled =
            isSubmitted ||
            isWithdrawalEligibilityLoading ||
            isExcluded ||
            !isKnown ||
            !isEligible;
          const fields = lineReturnFields[lineId] ?? {
            description: '',
            reason: '',
          };
          const vm: ReturnLineItemViewModel = {
            baseFieldId,
            channelSlug,
            eligibilityLine: eligibilityLine ?? null,
            fields,
            index,
            isEligible,
            isKnown,
            isLineDisabled,
            isMobileView,
            isSelected: Boolean(selectedLineIds[lineId]),
            isWithdrawalEligibilityLoading,
            languageCode: languageCode as LanguageCodeEnum,
            orderChannelSlug: order.channel.slug,
            orderLine,
            taxMessage,
            voucherType,
          };

          return (
            <ReturnLineItem
              key={`return-line-${lineId}-${index}`}
              allOrderLines={orderLines}
              imageLoader={imageLoader}
              onDescriptionChange={(value) =>
                updateLineField(lineId, { description: value })
              }
              onReasonChange={(value) =>
                updateLineField(lineId, { reason: value })
              }
              onToggle={(checked) => setLineSelected(lineId, checked)}
              reasonOptions={reasonOptions}
              vm={vm}
            />
          );
        })}
      </Box>

      <ReturnSubmitFooter
        isGuestMode={isGuestMode}
        isSubmitDisabled={isSubmitDisabled}
        isSubmitting={isSubmitting}
        onCancel={goBackToOrderDetails}
        onRecaptchaChange={setSubmitRecaptchaToken}
        onRecaptchaExpired={handleSubmitRecaptchaExpired}
        onSubmit={handleRequestSubmit}
        submitErrorMessage={submitErrorMessage}
        submitRecaptchaKey={submitRecaptchaKey}
      />

      <ReturnConfirmSubmitModal
        isOpen={isReturnConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => setIsReturnConfirmOpen(false)}
        onConfirm={performReturnSubmit}
      />
    </>
  );
};

OrderReturnView.getLayout = (page: ReactElement) => {
  return (
    <DraftModeContext.Provider value={page.props.draftMode}>
      <MyOrdersLayout
        cmsData={page.props.cmsDefaultLayoutData}
        header={AM.myAccountMessages.orderReturn}
        withDetails
      >
        {page}
      </MyOrdersLayout>
    </DraftModeContext.Provider>
  );
};
